// @file Dashboard settings store
// It wraps the dashboard settings Vuex store into a Pinia store.
import { getBackendState } from '@@/bits/backend_state'
import {
  isPaidBilling,
  isPaidLibraryBilling,
  isWebBilling,
  normalizedOppositePaymentSchedule,
  normalizedPaymentSchedule,
} from '@@/bits/billing_helper'
import { ALERT_ICON, THINKING_FACE_ICON } from '@@/bits/confirmation_dialog'
import { getCurrentTenant } from '@@/bits/current_tenant'
import currentUser from '@@/bits/current_user'
import { captureException, captureFetchException, setScope } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { p__, translatePrice, __ } from '@@/bits/intl'
import { reload } from '@@/bits/location'
import { getVuexStore } from '@@/bits/pinia'
import { PlanType } from '@@/bits/plans_helper'
import * as PadletApi from '@@/dashboard/padlet_api'
import {
  ContentSafetyEntity,
  LibraryMembershipTier,
  LibraryType,
  RosteringProvider,
  SubscriptionStatus,
  UserAccountType,
} from '@@/enums'
import { Billing as BillingApi } from '@@/payment/padlet_api'
import { useContentSafetySettingsStore } from '@@/pinia/content_safety_settings'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import { ConfirmationDialogLayoutVariant, useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import { useLibraryPlansStore } from '@@/pinia/library_plans'
import { usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { useSettingsBackpackPermissionsStore } from '@@/pinia/settings_backpack_permissions'
import { useSettingsBriefcasePermissionsStore } from '@@/pinia/settings_briefcase_permissions'
import { useSettingsDevelopersStore } from '@@/pinia/settings_developers'
import { useSettingsIntegrationsStore } from '@@/pinia/settings_integrations'
import { useSettingsIntegrationPermissionsStore } from '@@/pinia/settings_integration_permissions'
import { useSettingsLibraryAnalyticsStore } from '@@/pinia/settings_library_analytics_store'
import { useSettingsLibraryConnectionsStore } from '@@/pinia/settings_library_connections'
import { useSettingsLibraryInfoStore } from '@@/pinia/settings_library_info'
import { useSettingsLibraryMembersStore } from '@@/pinia/settings_library_members'
import { useSettingsLibrarySecurityStore } from '@@/pinia/settings_library_security'
import { SettingView, useSettingsNavigationStore } from '@@/pinia/settings_navigation'
import { useSettingsNotificationsStore } from '@@/pinia/settings_notifications'
import { useSettingsSchoolPermissionsStore } from '@@/pinia/settings_school_permissions'
import { useSettingsTeamPermissionsStore } from '@@/pinia/settings_team_permissions'
import { useSettingsTenantAnalyticsStore } from '@@/pinia/settings_tenant_analytics'
import { useSettingsTenantInfoStore } from '@@/pinia/settings_tenant_info'
import { useSettingsUserDashboardStore } from '@@/pinia/settings_user_dashboard'
import { useSettingsUserInfoStore } from '@@/pinia/settings_user_info'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type {
  Billing,
  EstimateCancellationRefundResult,
  EstimateSwitchingCostResult,
  EstimateTeamNeonDowngradeRefund,
  Library,
  LibraryBilling,
  LibraryId,
  LibraryInvoice,
  LibraryUserQuotaInfo,
  Plan,
  StairstepPriceTier,
  Tenant,
  User,
  UserOptions,
} from '@@/types'
import type { DashboardSettingsState } from '@@/vuexstore/dashboard_settings'
import type { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { configure as configureArvo } from '@padlet/arvo'
import { merge, sortBy, upperFirst } from 'es-toolkit'
import { defineStore } from 'pinia'
import type { ComputedRef, Ref } from 'vue'
import { computed, ref } from 'vue'

enum ScheduledChange {
  SwitchToAnnual = 'SwitchToAnnual',
  SwitchToMonthly = 'SwitchToMonthly',
  DowngradeToNeon = 'DowngradeToNeon',
  DowngradeToSilver = 'DowngradeToSilver',
  DowngradeToGold = 'DowngradeToGold',
  SwitchToPlatinum = 'SwitchToPlatinum',
  SwitchToGold = 'SwitchToGold',
}

enum LibraryScheduledChange {
  SwitchToAnnual = 'SwitchToAnnual',
  SwitchToMonthly = 'SwitchToMonthly',
  DowngradeToTeamNeon = 'DowngradeToTeamNeon',
  ChangePlanQuantity = 'ChangePlanQuantity',
}

enum GenericStep {
  AboveQuota = 'AboveQuota',
  ChooseDate = 'ChooseDate',
  Now = 'Now',
  NowDifferentCurrency = 'NowDifferentCurrency',
  ConfirmNow = 'ConfirmNow',
  Later = 'Later',
  ConfirmLater = 'ConfirmLater',
  // Gold
  ChooseDateGold = 'ChooseDateGold',
  NowGold = 'NowGold',
  NowGoldDifferentCurrency = 'NowGoldDifferentCurrency',
  LaterGold = 'LaterGold',
  ConfirmNowGold = 'ConfirmNowGold',
  ConfirmNowGoldDifferentCurrency = 'ConfirmNowGoldDifferentCurrency',
  ConfirmLaterGold = 'ConfirmLaterGold',
  // Silver
  ChooseDateSilver = 'ChooseDateSilver',
  NowSilver = 'NowSilver',
  NowSilverDifferentCurrency = 'NowSilverDifferentCurrency',
  LaterSilver = 'LaterSilver',
  ConfirmNowSilver = 'ConfirmNowSilver',
  ConfirmNowSilverDifferentCurrency = 'ConfirmNowSilverDifferentCurrency',
  ConfirmLaterSilver = 'ConfirmLaterSilver',
  // Reseller Switch to standard Platinum
  ChooseScheduleResellerPlatinum = 'ChooseScheduleResellerPlatinum',
  ConfirmResellerPlatinumMonthly = 'ConfirmResellerPlatinumMonthly',
  ConfirmResellerPlatinumAnnual = 'ConfirmResellerPlatinumAnnual',
  SuccessResellerPlatinum = 'SuccessResellerPlatinum',
  // Reseller Switch to standard Gold
  ChooseScheduleResellerGold = 'ChooseScheduleResellerGold',
  ConfirmResellerGoldMonthly = 'ConfirmResellerGoldMonthly',
  ConfirmResellerGoldAnnual = 'ConfirmResellerGoldAnnual',
  SuccessResellerGold = 'SuccessResellerGold',
}

type DowngradeStep = GenericStep

type CrossgradeStep = Exclude<GenericStep, typeof GenericStep.AboveQuota>

enum LibraryGenericStep {
  ChooseDate = 'ChooseDate',
  Now = 'Now',
  ConfirmNow = 'ConfirmNow',
  Later = 'Later',
  ConfirmLater = 'ConfirmLater',
}

type LibraryDowngradeStep = LibraryGenericStep

const unpaidInvoiceErrorMessage = __(
  'Sorry, we are not able to process your request because your last invoice is unpaid. Please update your payment method and try again.',
)

const unableToProcessErrorMessage = __("We're sorry, we were unable to process your request. Please try again later.")

const useDashboardSettingsStore = defineStore('dashboardSettings', () => {
  const settingsNavigationStore = useSettingsNavigationStore()

  // These refs are writable because Vuex actions still own the writes until Phase 3.
  // State
  const user = ref<User>({ ...currentUser })
  const userOptions = ref<UserOptions>({})
  const tenant = ref<Tenant>({ ...getCurrentTenant() })
  const billing = ref<Billing | null>(null)
  const invoices = ref<LibraryInvoice[]>([])
  const isProcessingDowngrade = ref(false)
  const downgradeError = ref('')
  const cancelReasonText = ref('')
  const isProcessingCrossgrade = ref(false)
  const crossgradeError = ref('')
  const xDowngradeModal = ref(false)
  const downgradeStep = ref<DowngradeStep | null>(null)
  const isLoadingCancellationRefundInformation = ref(false)
  const xCrossgradeModal = ref(false)
  const crossgradeStep = ref<CrossgradeStep | null>(null)
  const isLoadingCrossgradeInformation = ref(false)
  const isLoadingGoldDowngradeInformation = ref(false)
  const isLoadingSilverDowngradeInformation = ref(false)
  const estimatedCrossgradeCost = ref<EstimateSwitchingCostResult | null>(null)
  const estimatedGoldDowngradeCost = ref<EstimateSwitchingCostResult | null>(null)
  const estimatedSilverDowngradeCost = ref<EstimateSwitchingCostResult | null>(null)
  const isOnline = ref<boolean>(true)
  const unsortedLibraries = ref<Library[]>([])
  const libraries = computed({
    get: (): Library[] => sortBy(unsortedLibraries.value, ['joinedAt']),
    set: (value: Library[]) => {
      unsortedLibraries.value = value
    },
  })
  const isLoadingLibraryBillingInformation = ref(false)
  const libraryBilling = ref<LibraryBilling | null>(null)
  const libraryBillingError = ref('')
  const isLoadingLibraryInvoices = ref(false)
  const libraryInvoices = ref<LibraryInvoice[]>([])
  const libraryInvoicesError = ref('')
  const xLibraryDowngradeModal = ref(false)
  const xClassroomLibraryDowngradeModal = ref(false)
  const libraryDowngradeStep = ref<LibraryDowngradeStep | null>(null)
  const libraryDowngradeError = ref('')
  const isProcessingLibraryDowngrade = ref(false)
  const isLoadingTeamNeonDowngradeInformation = ref(false)
  const isLoadingLibraryChangeCardUrl = ref(false)
  const estimatedTeamNeonDowngradeRefund = ref<EstimateTeamNeonDowngradeRefund | null>(null)
  const estimatedCancellationRefund = ref<EstimateCancellationRefundResult | null>(null)
  const googleApiKeyForDrive = ref<string | null>(null)
  const googleAppId = ref<string | null>(null)
  const googleApiKeyForMap = ref<string | null>(null)
  const currentCountryCode = ref('')

  function showPlanDowngradeModal(step: DowngradeStep): void {
    xDowngradeModal.value = true
    downgradeStep.value = step
  }

  function showPlanCrossgradeModal(step: CrossgradeStep): void {
    xCrossgradeModal.value = true
    crossgradeStep.value = step
  }

  // Getters
  const isBriefcase = computed<boolean>(() => tenant.value.type === 'business')
  const isSchool = computed<boolean>(() => tenant.value.type === 'school')
  const hasEmail = computed<boolean>(() => !!user.value.email)
  const isNative = computed<boolean>(() => user.value.tenant_id === 1)
  const isStudent = computed<boolean>(() => user.value.role === 'member')
  const pageFilter = computed(() => {
    if (isNative.value) {
      return settingsNavigationStore.currentView
    }
    return getBackendState().filter
  })
  const userId = computed((): number => user.value.id)
  const isCurrentUserTeacherOrStaff = computed((): boolean => {
    return user.value.account_type === UserAccountType.TEACHER || user.value.account_type === UserAccountType.STAFF
  })
  const currentUserEmailMatchesNonNativeUser = computed((): boolean => user.value.emailMatchesNonNativeUser ?? false)
  const personalSubscriptionStatus = computed((): SubscriptionStatus | null => {
    if (!isAppUsing('affiliateSignupLink') && !isAppUsing('selfServeFreeTrial')) return null

    if (user.value.membership_tier === PlanType.Trial) return SubscriptionStatus.OnTrial
    if (user.value.is_trial_expired === true) return SubscriptionStatus.TrialExpired
    return null
  })

  const defaultLibrary = computed((): Library | undefined => {
    // TODO: replace user with currentUser
    const defaultLibraryId = user.value.defaultLibraryId
    if (defaultLibraryId === undefined) return undefined

    // Personal library currently does not exist
    const PERSONAL_LIBRARY_ID = ''
    if (defaultLibraryId === PERSONAL_LIBRARY_ID) return undefined

    return libraries.value.find((library: Library) => library.id.toString() === defaultLibraryId)
  })

  const isArsPlan = computed<boolean>((): boolean => {
    const currentBilling = billing.value
    if (!currentBilling || currentBilling.tier === 'neon') return false
    return currentBilling.plan.currency === 'ars'
  })

  const xArsPlanDeprecation = computed<boolean>((): boolean => {
    return isArsPlan.value && isAppUsing('deprecateArsPlans')
  })

  const personalTrialDaysLeft = computed((): number => user.value.trial_days_left ?? 0)

  // Actions
  function changeConnectivity(value: boolean): void {
    isOnline.value = value
  }

  function goBack(): void {
    settingsNavigationStore.goBackToReferrerOrDashboard()
  }

  function startOffboarding(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('startOffboarding', { root: true })
  }

  function toggleDarkMode(isDarkMode: boolean): void {
    useDarkModeStore().toggleDarkMode(isDarkMode)
  }

  function updateCurrentTenant(payload: Partial<Tenant>): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('updateCurrentTenant', payload, { root: true })
  }

  function updateUserOptions(payload: Partial<UserOptions>): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('updateUserOptions', payload, { root: true })
  }

  function verifyIdentity(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('verifyIdentity', { root: true })
  }

  const { isBillingInitialized, isCancellingSoon, isNeonTier } = usePersonalBilling(billing, invoices)

  const isPurchasedViaReseller = computed<boolean>((): boolean => {
    if (!isPaidBilling(billing.value) || billing.value.is_sponsored) return false
    return billing.value.status.is_purchased_via_reseller
  })

  const libraryComposable = useLibrary({ libraries })
  const {
    canInviteMembers,
    canInviteSchoolAdmins,
    canInviteSchoolStudents,
    canInviteSchoolTeachers,
    canManageMembers,
    canUpdateLibraryInfo,
    canSetupLti,
    canSetupGoogleClassroom,
    canSetupGoogleWorkspace,
    canSetupMicrosoftTeams,
    currentLibrary,
    isClassroomLibrary,
    isCurrentUserAtLeastLibraryTeacher,
    isCurrentUserLibraryAdmin,
    isCurrentUserLibraryOwner,
    isCurrentUserPartOfSchoolLibrary,
    isRosteringEnabled,
    rosteringProvider,
    isSchoolLibrary,
    isTeamLibrary,
    libraryCanManageLicenses,
    libraryId,
    libraryMakersCount,
    libraryName,
    librarySlug,
    libraryUserQuota,
    libraryWallsUsed,
    ownedLibraries,
    trialDaysLeft,
    isPdOrgLibrary,
    maxAdminQuota,
    maxTeacherQuota,
    maxTeacherAndAdminQuota,
    exceededAdminQuota,
    exceededTeacherQuota,
    fetchViewableLibraries,
  } = libraryComposable

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async function initializeState(startingState: any): Promise<void> {
    if (startingState?.user == null) {
      captureException(new Error('Dashboard settings initializeState called without starting state'))
      return
    }
    const { arvoConfig, ...initialState } = startingState
    setScope({ userId: initialState.user.id })
    configureArvo(arvoConfig)
    void getVuexStore<DashboardSettingsState>()?.dispatch('updateCurrentTenant', initialState.tenant, { root: true })
    initializePageState(initialState)
    if (isNative.value === true) {
      fetchViewableLibraries()
    }
    if (isAppUsing('googleAppLicensing')) {
      void getVuexStore<DashboardSettingsState>()?.dispatch('validateGoogleAppLicense', null, { root: true })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function initializePageState(fetchedState: any): void {
    user.value = fetchedState.user
    userOptions.value = fetchedState.userOptions
    unsortedLibraries.value = fetchedState.libraries
    googleApiKeyForDrive.value = fetchedState.googleApiKeyForDrive
    googleAppId.value = fetchedState.googleAppId
    googleApiKeyForMap.value = fetchedState.googleApiKeyForMap
    currentCountryCode.value = fetchedState.currentCountryCode
    switch (pageFilter.value) {
      case 'basic':
        void useSettingsUserInfoStore().fetchUserInfo()
        break
      case 'padlet_belts':
        void useSettingsUserInfoStore().fetchUserInfo()
        break
      case 'dashboard':
        void useSettingsUserDashboardStore().initialize()
        break
      case 'org_basic':
        useSettingsTenantInfoStore().initialize()
        break
      case 'developers':
        void useSettingsDevelopersStore().fetchApiToken()
        break
      case 'billing':
        void getVuexStore<DashboardSettingsState>()?.dispatch('initializeBillingSettings', null, { root: true })
        break
      case 'delete':
        void getVuexStore<DashboardSettingsState>()?.dispatch('initializeBillingSettings', null, { root: true })
        break
      case 'org_permissions':
        if (isBriefcase.value) {
          useSettingsBriefcasePermissionsStore().initialize(fetchedState.tenant)
        } else {
          useSettingsBackpackPermissionsStore().initialize(fetchedState.tenant)
        }
        break
      case 'org_content_safety':
        if (isSchool.value) {
          useContentSafetySettingsStore().initializeState(ContentSafetyEntity.Tenant, fetchedState.tenant.id ?? '')
        }
        break
      case 'org_analytics':
        useSettingsTenantAnalyticsStore().initialize(fetchedState.tenant)
        break
      case 'library_info':
        void useSettingsLibraryInfoStore().fetchLibraryInfo()
        break
      case 'library_security':
      case 'library_access':
        useSettingsLibrarySecurityStore().initialize()
        break
      case 'library_connections':
        useSettingsLibraryConnectionsStore().initialize()
        break
      case 'library_members':
        void useSettingsLibraryMembersStore().fetchLibraryMemberships()
        void useSettingsLibraryMembersStore().fetchPendingInvitations()
        void getVuexStore<DashboardSettingsState>()?.dispatch('fetchLibraryBilling', null, { root: true })
        break
      case 'library_content_safety_settings':
        useContentSafetySettingsStore().initializeState(
          ContentSafetyEntity.Library,
          currentLibrary.value?.id?.toString() ?? '',
        )
        break
      case 'library_billing':
        void getVuexStore<DashboardSettingsState>()?.dispatch('initializeLibraryBillingSettings', null, { root: true })
        break
      case 'library_delete':
        void getVuexStore<DashboardSettingsState>()?.dispatch('initializeLibraryDeleteSettings', null, { root: true })
        break
      case 'library_permissions':
        if (currentLibrary.value?.libraryType === LibraryType.Team) {
          void useSettingsTeamPermissionsStore().initialize(currentLibrary.value)
        } else if (currentLibrary.value != null) {
          void useSettingsSchoolPermissionsStore().initialize(currentLibrary.value)
        }
        break
      case 'library_analytics':
        if (currentLibrary.value != null) {
          void useSettingsLibraryAnalyticsStore().initialize(currentLibrary.value)
        }
        break
      case 'notifications':
        useSettingsNotificationsStore().initialize()
        break
      case SettingView.Integrations:
        useSettingsIntegrationPermissionsStore().initialize()
        void useSettingsIntegrationsStore().fetchZapierApiToken()
        break
    }
  }

  function reloadPage(): void {
    reload()
  }

  async function fetchTenantPermissions(): Promise<void> {
    const tenantId = tenant.value.id
    try {
      const response = await PadletApi.TenantPermissions.fetch(tenantId)
      const fetchedTenant = (response.data as JsonAPIResource<Tenant>).attributes
      tenant.value = merge(tenant.value, fetchedTenant)
    } catch (e) {
      captureFetchException(e, { source: 'DashboardSettingsFetchTenantPermissions' })
    }
  }

  const {
    currentLibraryScheduledChange,
    estimatedTeamNeonDowngradeRefundAmount,
    hasLibraryScheduledChanges,
    hasPendingMakerCountUpdate,
    isLibraryClassroomGoldTier,
    isLibraryOnV2ClassroomPlan,
    isLibraryPaying,
    isLibraryTeamGoldTier,
    isLibraryTeamNeonTier,
    isLibraryWebPlan,
    isOfferedTeamCurrencyEqualToCurrentCurrency,
    librarySubscriptionStatus,
    libraryUsersBilledCount,
    isLibraryGoldLegacyTier,
    isLibraryMigratedPlan,
    isLibraryMigratedPlanOnTrial,
    currentOfferedLibraryPlan,
    classroomPlanCurrentTierPrice,
    changeStepLibraryDowngradeModal,
    closeClassroomLibraryDowngradeModal,
    closeLibraryDowngradeModal,
    downgradeLibraryToTeamNeonImmediately,
    loadAndRedirectToLibraryChangeCardUrl,
    scheduleLibraryDowngradeToTeamNeon,
    showLibraryDowngradeToTeamNeonModalCheckingQuota,
    showUndoLibraryScheduledChangeModal,
  } = useLibraryBilling({
    library: libraryComposable,
    estimatedTeamNeonDowngradeRefund,
    libraryBilling,
  })

  const {
    currentScheduledChange,
    currentScheduleGoldPlan,
    currentScheduleSilverPlan,
    displayedEstimatedCrossgradeCost,
    displayedEstimatedGoldDowngradeCost,
    displayedEstimatedSilverDowngradeCost,
    estimatedCancellationRefundAmount,
    oppositeSchedulePlan,
    hasScheduledChanges,
    isGoldLegacyTier,
    isMigratedPlan,
    isMigratedPlanOnTrial,
    isMobilePlan,
    isApplePlan,
    isOfferedPlanCurrencyEqualToUserPlanCurrency,
    isPlatinumLegacyTier,
    isPlatinumResellerTier,
    isWebPlan,
    cancelImmediately,
    closePlanCrossgradeModal,
    closePlanDowngradeModal,
    doNotRenew,
    downgradePlatinumToGoldDifferentCurrencyImmediately,
    downgradePlatinumToGoldImmediately,
    schedulePlatinumToGoldDowngrade,
    downgradeToSilverDifferentCurrencyImmediately,
    downgradeToSilverImmediately,
    scheduleToSilverDowngrade,
    scheduleResellerSwitch,
    scheduleSwitch,
    showPlanCrossgradeModalCheckingCurrency,
    showPlanDowngradeToGoldModalCheckingQuota,
    showPlanDowngradeToSilverModalCheckingQuota,
    showPlanDowngradeToNeonModalCheckingQuota,
    showScheduledChangeModal,
    switchDifferentCurrencyImmediately,
    switchImmediately,
    isLegacyTier,
  } = usePersonalPlans(billing, {
    cancelReasonText,
    crossgradeError,
    crossgradeStep,
    downgradeError,
    downgradeStep,
    estimatedCancellationRefund,
    estimatedCrossgradeCost,
    estimatedGoldDowngradeCost,
    estimatedSilverDowngradeCost,
    isLoadingCancellationRefundInformation,
    isLoadingCrossgradeInformation,
    isLoadingGoldDowngradeInformation,
    isLoadingSilverDowngradeInformation,
    isProcessingCrossgrade,
    isProcessingDowngrade,
    xCrossgradeModal,
    xDowngradeModal,
  })

  function setDowngradeRequestError({ errorMessage }: { errorMessage?: string } = {}): void {
    downgradeError.value = errorMessage || unableToProcessErrorMessage
  }

  function setCrossgradeRequestError({ errorMessage }: { errorMessage?: string } = {}): void {
    crossgradeError.value = errorMessage || unableToProcessErrorMessage
  }

  function setLibraryDowngradeError({ errorMessage }: { errorMessage?: string } = {}): void {
    libraryDowngradeError.value = errorMessage || unableToProcessErrorMessage
  }

  function handleRefundEstimationErrorInDowngrade(e: unknown): void {
    if ((e as { status?: number }).status === 422) {
      setDowngradeRequestError({
        errorMessage: unpaidInvoiceErrorMessage,
      })
    } else {
      setDowngradeRequestError({})
    }
  }

  function handleRefundEstimationErrorInCrossgrade(e: unknown): void {
    if ((e as { status?: number }).status === 422) {
      setCrossgradeRequestError({
        errorMessage: unpaidInvoiceErrorMessage,
      })
    } else {
      setCrossgradeRequestError({})
    }
  }

  async function fetchRefunds(onError: (e: unknown) => void): Promise<void> {
    isLoadingCancellationRefundInformation.value = true
    try {
      const response: JsonAPIResponse<EstimateCancellationRefundResult> = await BillingApi.estimateCancellationRefund()
      const estimatedRefund = (response?.data as JsonAPIResource<EstimateCancellationRefundResult>)?.attributes
      estimatedCancellationRefund.value = estimatedRefund
    } catch (e) {
      onError(e)
    } finally {
      isLoadingCancellationRefundInformation.value = false
    }
  }

  function showAboveQuotaConfirmationDialog({
    title,
    body,
    afterConfirm,
  }: {
    title: string
    body: string
    afterConfirm: () => unknown
  }): void {
    useGlobalConfirmationDialogStore().openConfirmationDialog({
      ...THINKING_FACE_ICON,
      layoutVariant: useWindowSizeStore().isPhone
        ? ConfirmationDialogLayoutVariant.Drawer
        : ConfirmationDialogLayoutVariant.Large,
      title,
      body,
      confirmButtonText: __('Downgrade anyways'),
      cancelButtonText: __('Nevermind'),
      afterConfirmCallbacks: [async () => await afterConfirm()],
    })
  }

  function showAboveNeonQuotaConfirmationDialog({
    wallsUsed,
    tierQuota,
    afterConfirm,
  }: {
    wallsUsed: number
    tierQuota: number
    afterConfirm: () => unknown
  }): void {
    const body =
      isLegacyTier.value(billing.value?.tier ?? '') === true
        ? p__(
            'currentMembershipTier will either be Silver, Gold or Platinum',
            'You currently have %{amount} padlets, but our Neon tier only supports %{neonQuota}. If you downgrade <strong>you will have %{daysRemaining} days to be at or below your quota or your padlets will be put into an uneditable frozen state.</strong> <a href="%{padletFrozenArticleUrl}" target="_blank" rel="noopener nofollow" class="oz-text-link-light-primary dark:oz-text-link-dark-primary">Learn more.</a> <br/><br/>This legacy %{currentMembershipTier} membership tier will no longer be available after downgrading.',
            {
              amount: wallsUsed,
              neonQuota: tierQuota,
              daysRemaining: 7,
              currentMembershipTier: upperFirst(currentUser.membership_tier ?? ''),
              padletFrozenArticleUrl:
                'https://padlet.help/article/1lkpvciur8-freezing-padlets-when-your-account-is-above-its-quota',
            },
          )
        : __(
            'You currently have %{amount} padlets, but our Neon tier only supports %{neonQuota}. If you downgrade <strong>you will have %{daysRemaining} days to be at or below your quota or your padlets will be put into an uneditable frozen state.</strong> <a href="%{padletFrozenArticleUrl}" target="_blank" rel="noopener nofollow" class="oz-text-link-light-primary dark:oz-text-link-dark-primary">Learn more.</a>',
            {
              amount: wallsUsed,
              neonQuota: tierQuota,
              daysRemaining: 7,
              padletFrozenArticleUrl:
                'https://padlet.help/article/1lkpvciur8-freezing-padlets-when-your-account-is-above-its-quota',
            },
          )
    showAboveQuotaConfirmationDialog({
      title: __('You’re above this tier’s padlet limit'),
      body,
      afterConfirm,
    })
  }

  function showAboveGoldQuotaConfirmationDialog({
    wallsUsed,
    tierQuota,
    afterConfirm,
  }: {
    wallsUsed: number
    tierQuota: number
    afterConfirm: () => unknown
  }): void {
    const body =
      isLegacyTier.value(billing.value?.tier ?? '') === true
        ? p__(
            'currentMembershipTier will either be Silver, Gold or Platinum',
            'You currently have %{amount} padlets, but our Gold tier only supports %{goldQuota}. If you downgrade <strong>you will have %{daysRemaining} days to be at or below your quota or your padlets will be put into an uneditable frozen state.</strong> <a href="%{padletFrozenArticleUrl}" target="_blank" rel="noopener nofollow" class="oz-text-link-light-primary dark:oz-text-link-dark-primary">Learn more.</a> <br/><br/>This legacy %{currentMembershipTier} membership tier will no longer be available after downgrading.',
            {
              amount: wallsUsed,
              goldQuota: tierQuota,
              daysRemaining: 7,
              currentMembershipTier: upperFirst(currentUser.membership_tier ?? ''),
              padletFrozenArticleUrl:
                'https://padlet.help/article/1lkpvciur8-freezing-padlets-when-your-account-is-above-its-quota',
            },
          )
        : __(
            'You currently have %{amount} padlets, but our Gold tier only supports %{goldQuota}. If you downgrade <strong>you will have %{daysRemaining} days to be at or below your quota or your padlets will be put into an uneditable frozen state.</strong> <a href="%{padletFrozenArticleUrl}" target="_blank" rel="noopener nofollow" class="oz-text-link-light-primary dark:oz-text-link-dark-primary">Learn more.</a>',
            {
              amount: wallsUsed,
              goldQuota: tierQuota,
              daysRemaining: 7,
              padletFrozenArticleUrl:
                'https://padlet.help/article/1lkpvciur8-freezing-padlets-when-your-account-is-above-its-quota',
            },
          )
    showAboveQuotaConfirmationDialog({
      title: __('You’re above this tier’s padlet limit'),
      body,
      afterConfirm,
    })
  }

  function showAboveSilverQuotaConfirmationDialog({
    wallsUsed,
    tierQuota,
    afterConfirm,
  }: {
    wallsUsed: number
    tierQuota: number
    afterConfirm: () => unknown
  }): void {
    const body =
      isLegacyTier.value(billing.value?.tier ?? '') === true
        ? p__(
            'currentMembershipTier will either be Silver, Gold or Platinum',
            'You currently have %{amount} padlets, but our Silver tier only supports %{silverQuota}. If you downgrade <strong>you will have %{daysRemaining} days to be at or below your quota or your padlets will be put into an uneditable frozen state.</strong> <a href="%{padletFrozenArticleUrl}" target="_blank" rel="noopener nofollow" class="oz-text-link-light-primary dark:oz-text-link-dark-primary">Learn more.</a> <br/><br/>This legacy %{currentMembershipTier} membership tier will no longer be available after downgrading.',
            {
              amount: wallsUsed,
              silverQuota: tierQuota,
              daysRemaining: 7,
              currentMembershipTier: upperFirst(currentUser.membership_tier ?? ''),
              padletFrozenArticleUrl:
                'https://padlet.help/article/1lkpvciur8-freezing-padlets-when-your-account-is-above-its-quota',
            },
          )
        : __(
            'You currently have %{amount} padlets, but our Silver tier only supports %{silverQuota}. If you downgrade <strong>you will have %{daysRemaining} days to be at or below your quota or your padlets will be put into an uneditable frozen state.</strong> <a href="%{padletFrozenArticleUrl}" target="_blank" rel="noopener nofollow" class="oz-text-link-light-primary dark:oz-text-link-dark-primary">Learn more.</a>',
            {
              amount: wallsUsed,
              silverQuota: tierQuota,
              daysRemaining: 7,
              padletFrozenArticleUrl:
                'https://padlet.help/article/1lkpvciur8-freezing-padlets-when-your-account-is-above-its-quota',
            },
          )
    showAboveQuotaConfirmationDialog({
      title: __('You’re above this tier’s padlet limit'),
      body,
      afterConfirm,
    })
  }

  function showPlanDowngradeAtOrBelowLimitConfirmationDialog({ afterConfirm }: { afterConfirm: () => unknown }): void {
    if (isLegacyTier.value(billing.value?.tier ?? '') === true) {
      useGlobalConfirmationDialogStore().openConfirmationDialog({
        ...THINKING_FACE_ICON,
        layoutVariant: useWindowSizeStore().isPhone
          ? ConfirmationDialogLayoutVariant.Drawer
          : ConfirmationDialogLayoutVariant.Large,
        title: __('Legacy plan will not be available after downgrading'),
        body: p__(
          'currentMembershipTier will either be Silver, Gold or Platinum',
          'You are currently on a legacy %{currentMembershipTier} membership. If you downgrade, this tier will no longer be available. Only our standard %{currentMembershipTier} membership tier will be available.',
          {
            currentMembershipTier: upperFirst(currentUser.membership_tier ?? ''),
          },
        ),
        confirmButtonText: __('Downgrade anyways'),
        cancelButtonText: __('Nevermind'),
        afterConfirmCallbacks: [async () => await afterConfirm()],
      })
    } else {
      void afterConfirm()
    }
  }

  function showLibraryPlanDowngradeAtOrBelowLimitConfirmationDialog({
    afterConfirm,
  }: {
    afterConfirm: () => unknown
  }): void {
    if (isLibraryGoldLegacyTier.value) {
      useGlobalConfirmationDialogStore().openConfirmationDialog({
        ...THINKING_FACE_ICON,
        layoutVariant: useWindowSizeStore().isPhone
          ? ConfirmationDialogLayoutVariant.Drawer
          : ConfirmationDialogLayoutVariant.Large,
        title: __('Legacy plan will not be available after downgrading'),
        body: p__(
          'currentMembershipTier will either be Team Gold or Classroom Gold',
          'You are currently on a legacy %{currentMembershipTier} membership. If you downgrade, this tier will no longer be available. Only our standard %{currentMembershipTier} membership tier will be available.',
          {
            currentMembershipTier: isClassroomLibrary.value ? 'Classroom Gold' : 'Team Gold',
          },
        ),
        confirmButtonText: __('Downgrade anyways'),
        cancelButtonText: __('Nevermind'),
        afterConfirmCallbacks: [async () => await afterConfirm()],
      })
    } else {
      void afterConfirm()
    }
  }

  function showCancelSubscriptionConfirmationDialog({ afterConfirm }: { afterConfirm: () => unknown }): void {
    let body = __(
      'You will still have access to your existing padlets but you will no longer be able to make new ones.',
    )
    if (isLibraryGoldLegacyTier.value) {
      if (isClassroomLibrary.value) {
        body = __(
          'You will still have access to your existing padlets but you will no longer be able to make new ones. This legacy Classroom Gold membership tier will no longer be available after downgrading.',
        )
      } else {
        body = __(
          'You will still have access to your existing padlets but you will no longer be able to make new ones. This legacy Team Gold membership tier will no longer be available after downgrading.',
        )
      }
    }

    void useGlobalConfirmationDialogStore().openConfirmationDialog({
      ...THINKING_FACE_ICON,
      layoutVariant: useWindowSizeStore().isPhone
        ? ConfirmationDialogLayoutVariant.Drawer
        : ConfirmationDialogLayoutVariant.Large,
      title: __('Cancel subscription?'),
      body,
      confirmButtonText: __('Continue'),
      cancelButtonText: __('Nevermind'),
      afterConfirmCallbacks: [async () => await afterConfirm()],
    })
  }

  function showFailedUndoScheduledChangeDialog({ afterConfirm }: { afterConfirm: () => unknown }): void {
    useGlobalConfirmationDialogStore().openConfirmationDialog({
      ...ALERT_ICON,
      title: __('Undo failed'),
      body: __('Oops! Something went wrong. We were unable to process your undo request.'),
      confirmButtonText: __('Try again'),
      cancelButtonText: __('Cancel'),
      afterConfirmCallbacks: [async () => await afterConfirm()],
      forceFullWidthButtons: true,
      shouldFadeIn: false,
    })
  }

  return {
    // State

    // Getters
    billing,
    canInviteMembers,
    canInviteSchoolAdmins,
    canInviteSchoolStudents,
    canInviteSchoolTeachers,
    canManageMembers,
    canUpdateLibraryInfo,
    canSetupLti,
    canSetupGoogleClassroom,
    canSetupGoogleWorkspace,
    canSetupMicrosoftTeams,
    cancelReasonText,
    crossgradeError,
    crossgradeStep,
    currentLibrary,
    currentLibraryScheduledChange,
    currentScheduledChange,
    currentScheduleGoldPlan,
    currentScheduleSilverPlan,
    displayedEstimatedCrossgradeCost,
    displayedEstimatedGoldDowngradeCost,
    displayedEstimatedSilverDowngradeCost,
    downgradeError,
    downgradeStep,
    estimatedCancellationRefund,
    estimatedCancellationRefundAmount,
    estimatedCrossgradeCost,
    estimatedGoldDowngradeCost,
    estimatedSilverDowngradeCost,
    estimatedTeamNeonDowngradeRefund,
    estimatedTeamNeonDowngradeRefundAmount,
    hasLibraryScheduledChanges,
    hasPendingMakerCountUpdate,
    hasScheduledChanges,
    hasEmail,
    invoices,
    isCurrentUserTeacherOrStaff,
    isBillingInitialized,
    isBriefcase,
    isCancellingSoon,
    isClassroomLibrary,
    isCurrentUserAtLeastLibraryTeacher,
    isCurrentUserLibraryAdmin,
    isCurrentUserLibraryOwner,
    isPurchasedViaReseller,
    currentUserEmailMatchesNonNativeUser,
    personalSubscriptionStatus,
    isArsPlan,
    xArsPlanDeprecation,
    personalTrialDaysLeft,
    isCurrentUserPartOfSchoolLibrary,
    isGoldLegacyTier,
    isLibraryClassroomGoldTier,
    isLibraryOnV2ClassroomPlan,
    isLibraryPaying,
    isLibraryTeamGoldTier,
    isLibraryTeamNeonTier,
    isLibraryMigratedPlan,
    isLibraryMigratedPlanOnTrial,
    isLibraryWebPlan,
    isLoadingCancellationRefundInformation,
    isLoadingCrossgradeInformation,
    isLoadingLibraryBillingInformation,
    isLoadingLibraryChangeCardUrl,
    isLoadingLibraryInvoices,
    isLoadingTeamNeonDowngradeInformation,
    isLoadingGoldDowngradeInformation,
    isLoadingSilverDowngradeInformation,
    isMigratedPlan,
    isMigratedPlanOnTrial,
    isNeonTier,
    isNative,
    isOfferedPlanCurrencyEqualToUserPlanCurrency,
    isOfferedTeamCurrencyEqualToCurrentCurrency,
    isOnline,
    isStudent,
    unsortedLibraries,
    isPlatinumLegacyTier,
    isPlatinumResellerTier,
    isProcessingCrossgrade,
    isMobilePlan,
    isApplePlan,
    isProcessingDowngrade,
    isProcessingLibraryDowngrade,
    isRosteringEnabled,
    isSchool,
    isSchoolLibrary,
    isTeamLibrary,
    isWebPlan,
    libraries,
    libraryBilling,
    libraryBillingError,
    libraryDowngradeError,
    libraryDowngradeStep,
    libraryId,
    libraryInvoices,
    libraryInvoicesError,
    libraryMakersCount,
    libraryName,
    librarySlug,
    librarySubscriptionStatus,
    libraryUserQuota,
    libraryUsersBilledCount,
    libraryWallsUsed,
    oppositeSchedulePlan,
    ownedLibraries,
    pageFilter,
    rosteringProvider,
    tenant,
    googleApiKeyForDrive,
    googleAppId,
    googleApiKeyForMap,
    currentCountryCode,
    trialDaysLeft,
    user,
    userId,
    defaultLibrary,
    userOptions,
    xClassroomLibraryDowngradeModal,
    xCrossgradeModal,
    xDowngradeModal,
    xLibraryDowngradeModal,
    isLibraryGoldLegacyTier,
    currentOfferedLibraryPlan,
    classroomPlanCurrentTierPrice,
    libraryCanManageLicenses,
    isPdOrgLibrary,
    maxAdminQuota,
    maxTeacherQuota,
    maxTeacherAndAdminQuota,
    exceededAdminQuota,
    exceededTeacherQuota,

    // Actions
    cancelImmediately,
    fetchTenantPermissions,
    initializePageState,
    initializeState,
    changeConnectivity,
    changeStepLibraryDowngradeModal,
    closeClassroomLibraryDowngradeModal,
    closeLibraryDowngradeModal,
    closePlanCrossgradeModal,
    closePlanDowngradeModal,
    doNotRenew,
    downgradeLibraryToTeamNeonImmediately,
    downgradePlatinumToGoldDifferentCurrencyImmediately,
    downgradePlatinumToGoldImmediately,
    downgradeToSilverDifferentCurrencyImmediately,
    downgradeToSilverImmediately,
    fetchViewableLibraries,
    goBack,
    loadAndRedirectToLibraryChangeCardUrl,
    reloadPage,
    scheduleLibraryDowngradeToTeamNeon,
    startOffboarding,
    toggleDarkMode,
    updateCurrentTenant,
    updateUserOptions,
    verifyIdentity,
    schedulePlatinumToGoldDowngrade,
    scheduleResellerSwitch,
    scheduleToSilverDowngrade,
    scheduleSwitch,
    showLibraryDowngradeToTeamNeonModalCheckingQuota,
    showPlanCrossgradeModal,
    showPlanCrossgradeModalCheckingCurrency,
    showPlanDowngradeModal,
    showPlanDowngradeToGoldModalCheckingQuota,
    showPlanDowngradeToSilverModalCheckingQuota,
    showPlanDowngradeToNeonModalCheckingQuota,
    showScheduledChangeModal,
    showUndoLibraryScheduledChangeModal,
    switchDifferentCurrencyImmediately,
    switchImmediately,
    setDowngradeRequestError,
    setCrossgradeRequestError,
    setLibraryDowngradeError,
    handleRefundEstimationErrorInDowngrade,
    handleRefundEstimationErrorInCrossgrade,
    fetchRefunds,
    showAboveQuotaConfirmationDialog,
    showAboveNeonQuotaConfirmationDialog,
    showAboveGoldQuotaConfirmationDialog,
    showAboveSilverQuotaConfirmationDialog,
    showPlanDowngradeAtOrBelowLimitConfirmationDialog,
    showLibraryPlanDowngradeAtOrBelowLimitConfirmationDialog,
    showCancelSubscriptionConfirmationDialog,
    showFailedUndoScheduledChangeDialog,
  }
})

interface LibraryComposable {
  canInviteMembers: ComputedRef<boolean>
  canInviteSchoolAdmins: ComputedRef<boolean>
  canInviteSchoolStudents: ComputedRef<boolean>
  canInviteSchoolTeachers: ComputedRef<boolean>
  canManageMembers: ComputedRef<boolean>
  canUpdateLibraryInfo: ComputedRef<boolean>
  canSetupLti: ComputedRef<boolean>
  canSetupGoogleClassroom: ComputedRef<boolean>
  canSetupGoogleWorkspace: ComputedRef<boolean>
  canSetupMicrosoftTeams: ComputedRef<boolean>
  currentLibrary: ComputedRef<Library | null>
  isClassroomLibrary: ComputedRef<boolean>
  isRosteringEnabled: ComputedRef<boolean>
  rosteringProvider: ComputedRef<string | null>
  libraryCanManageLicenses: ComputedRef<boolean>
  isCurrentUserAtLeastLibraryTeacher: ComputedRef<boolean>
  isCurrentUserLibraryAdmin: ComputedRef<boolean>
  isCurrentUserLibraryOwner: ComputedRef<boolean>
  isCurrentUserPartOfSchoolLibrary: ComputedRef<boolean>
  isSchoolLibrary: ComputedRef<boolean>
  isTeamLibrary: ComputedRef<boolean>
  libraryId: ComputedRef<LibraryId | null>
  libraryMakersCount: ComputedRef<number>
  libraryName: ComputedRef<string>
  librarySlug: ComputedRef<string>
  libraryUserQuota: ComputedRef<LibraryUserQuotaInfo | undefined>
  libraryWallsUsed: ComputedRef<number>
  ownedLibraries: ComputedRef<Library[]>
  trialDaysLeft: ComputedRef<number>
  isPdOrgLibrary: ComputedRef<boolean>
  maxAdminQuota: ComputedRef<number | null>
  maxTeacherQuota: ComputedRef<number | null>
  maxTeacherAndAdminQuota: ComputedRef<number | null>
  exceededAdminQuota: ComputedRef<boolean>
  exceededTeacherQuota: ComputedRef<boolean>

  fetchViewableLibraries: () => void
}

const useLibrary = ({ libraries }: { libraries: ComputedRef<Library[]> }): LibraryComposable => {
  // State

  // Getters
  const canInviteMembers = computed<boolean>(() => {
    // School libraries created via Skolon cannot invite people via the dashboard.
    // They have to be provisioned via Skolon SSO.
    if (currentLibrary.value?.skolonLinked === true) return false
    return currentLibrary.value?.libraryAccess?.canInviteMembers === true
  })
  const canInviteSchoolAdmins = computed<boolean>(
    () => currentLibrary.value?.libraryAccess?.canInviteSchoolAdmins ?? false,
  )
  const canInviteSchoolStudents = computed<boolean>(() => {
    // Prevent inviting students if this is a PD org custom school library
    if (currentLibrary.value?.customSchoolLibraryType === 'pd_org') return false
    return currentLibrary.value?.libraryAccess?.canInviteSchoolStudents ?? false
  })
  const canInviteSchoolTeachers = computed<boolean>(
    () => currentLibrary.value?.libraryAccess?.canInviteSchoolTeachers ?? false,
  )
  const canManageMembers = computed<boolean>(() => currentLibrary.value?.libraryAccess?.canManageMembers ?? false)
  const canUpdateLibraryInfo = computed<boolean>(
    () => currentLibrary.value?.libraryAccess?.canUpdateLibraryInfo ?? false,
  )
  const canSetupLti = computed<boolean>(() => currentLibrary.value?.libraryAccess?.canSetupLti ?? false)
  const canSetupGoogleClassroom = computed<boolean>(
    () => currentLibrary.value?.libraryAccess?.canSetupGoogleClassroom ?? false,
  )
  const canSetupGoogleWorkspace = computed<boolean>(
    () => currentLibrary.value?.libraryAccess?.canSetupGoogleWorkspace ?? false,
  )
  const canSetupMicrosoftTeams = computed<boolean>(
    () => currentLibrary.value?.libraryAccess?.canSetupMicrosoftTeams ?? false,
  )
  const currentLibrary = computed<Library | null>(() => {
    const library = libraries.value.find((library: Library) => library.id === libraryId.value)
    return library != null ? library : null
  })
  const isClassroomLibrary = computed<boolean>(() => currentLibrary.value?.libraryType === LibraryType.Classroom)
  const libraryCanManageLicenses = computed<boolean>(() => currentLibrary.value?.libraryCanManageLicenses ?? false)
  const isCurrentUserAtLeastLibraryTeacher = computed<boolean>(
    () => currentLibrary.value?.libraryAccess?.canUseLibraryAsTeacher ?? false,
  )
  const isCurrentUserLibraryAdmin = computed<boolean>(
    () => currentLibrary.value?.libraryAccess?.canUseLibraryAsAdmin ?? false,
  )
  const isCurrentUserLibraryOwner = computed<boolean>(
    () => currentLibrary.value?.libraryAccess?.canUseLibraryAsOwner ?? false,
  )
  const isRosteringEnabled = computed<boolean>(() => currentLibrary.value?.rosteringEnabled ?? false)
  const rosteringProvider = computed<string | null>(() => {
    if (!isRosteringEnabled.value) return null
    const rosteringSettings = currentLibrary.value?.rosteringSettings

    if (rosteringSettings?.clever) return RosteringProvider.Clever
    if (rosteringSettings?.googleAppLicensing) return RosteringProvider.GoogleAppLicensing
    if (rosteringSettings?.googleWorkspace) return RosteringProvider.GoogleWorkspace
    if (rosteringSettings?.oneroster) return RosteringProvider.OneRoster
    if (rosteringSettings?.classlink) return RosteringProvider.Classlink
    return null
  })
  const isSchoolLibrary = computed<boolean>(() => currentLibrary.value?.libraryType === LibraryType.School)
  const isTeamLibrary = computed<boolean>(() => currentLibrary.value?.libraryType === LibraryType.Team)
  const libraryId = computed<LibraryId | null>(() => useSettingsNavigationStore().currentLibraryId)
  const libraryMakersCount = computed<number>(() => currentLibrary.value?.makersCount ?? 0)
  const libraryName = computed<string>(() => currentLibrary.value?.name ?? '')
  const librarySlug = computed<string>(() => currentLibrary.value?.slug ?? '')
  const libraryUserQuota = computed<LibraryUserQuotaInfo | undefined>(() => currentLibrary.value?.userQuota)
  const libraryWallsUsed = computed<number>(() => currentLibrary.value?.quota.wallsUsed ?? 0)
  const ownedLibraries = computed<Library[]>(() =>
    libraries.value.filter((library: Library) => library.libraryAccess?.canUseLibraryAsOwner === true),
  )
  const trialDaysLeft = computed<number>(() => currentLibrary.value?.trialDaysLeft ?? 0)
  const isCurrentUserPartOfSchoolLibrary = computed((): boolean => {
    return libraries.value.some((library) => library.libraryType === LibraryType.School)
  })
  const isPdOrgLibrary = computed<boolean>(() => currentLibrary.value?.customSchoolLibraryType === 'pd_org')
  // Caps can be 0 (meaning no admins/teachers allowed) or null (meaning no cap set).
  // We must use != null instead of truthiness check to allow 0 as a valid cap value.
  const maxAdminQuota = computed<number | null>(() => {
    return libraryUserQuota.value?.maxAdminQuota ?? null
  })
  const maxTeacherQuota = computed<number | null>(() => {
    return libraryUserQuota.value?.maxTeacherQuota ?? null
  })
  // The combined teacher+admin seat cap. For subscription-capped schools this is
  // the billed seat count; for PD-org libraries it's the sum of the admin/teacher
  // props. Unlike maxTeacherQuota (a PD-org-only teacher sub-cap, null for
  // ordinary subscription schools) this resolves for every capped library, so
  // it's the right "purchased licenses" figure. See SchoolLibraryUserQuotas.
  const maxTeacherAndAdminQuota = computed<number | null>(() => {
    return libraryUserQuota.value?.maxTeacherAndAdminQuota ?? null
  })
  const exceededAdminQuota = computed<boolean>(() => {
    if (!isPdOrgLibrary.value || maxAdminQuota.value == null) return false
    return (libraryUserQuota.value?.adminQuotaUsed ?? 0) >= maxAdminQuota.value
  })
  const exceededTeacherQuota = computed<boolean>(() => {
    if (!isPdOrgLibrary.value || maxTeacherQuota.value == null) return false
    return (libraryUserQuota.value?.teacherQuotaUsed ?? 0) >= maxTeacherQuota.value
  })

  // Actions
  function fetchViewableLibraries(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('fetchViewableLibraries', null, { root: true })
  }

  return {
    // State

    // Getters
    canInviteMembers,
    canInviteSchoolAdmins,
    canInviteSchoolStudents,
    canInviteSchoolTeachers,
    canManageMembers,
    canUpdateLibraryInfo,
    canSetupLti,
    canSetupGoogleClassroom,
    canSetupGoogleWorkspace,
    canSetupMicrosoftTeams,
    currentLibrary,
    isClassroomLibrary,
    isRosteringEnabled,
    rosteringProvider,
    libraryCanManageLicenses,
    isCurrentUserAtLeastLibraryTeacher,
    isCurrentUserLibraryAdmin,
    isCurrentUserLibraryOwner,
    isCurrentUserPartOfSchoolLibrary,
    isSchoolLibrary,
    isTeamLibrary,
    libraryId,
    libraryMakersCount,
    libraryName,
    librarySlug,
    libraryUserQuota,
    libraryWallsUsed,
    ownedLibraries,
    trialDaysLeft,
    isPdOrgLibrary,
    maxAdminQuota,
    maxTeacherQuota,
    maxTeacherAndAdminQuota,
    exceededAdminQuota,
    exceededTeacherQuota,

    // Actions
    fetchViewableLibraries,
  }
}

interface LibraryBillingStateRefs {
  estimatedTeamNeonDowngradeRefund: Ref<EstimateTeamNeonDowngradeRefund | null>
  libraryBilling: Ref<LibraryBilling | null>
}

interface LibraryBillingComposable {
  currentLibraryScheduledChange: ComputedRef<LibraryScheduledChange | null>
  estimatedTeamNeonDowngradeRefundAmount: ComputedRef<string | null>
  hasLibraryScheduledChanges: ComputedRef<boolean>
  hasPendingMakerCountUpdate: ComputedRef<boolean>
  isLibraryClassroomGoldTier: ComputedRef<boolean>
  isLibraryOnV2ClassroomPlan: ComputedRef<boolean>
  isLibraryPaying: ComputedRef<boolean>
  isLibraryTeamGoldTier: ComputedRef<boolean>
  isLibraryTeamNeonTier: ComputedRef<boolean>
  isLibraryWebPlan: ComputedRef<boolean>
  isOfferedTeamCurrencyEqualToCurrentCurrency: ComputedRef<boolean>
  librarySubscriptionStatus: ComputedRef<SubscriptionStatus | null>
  libraryUsersBilledCount: ComputedRef<number>
  isLibraryGoldLegacyTier: ComputedRef<boolean>
  isLibraryMigratedPlan: ComputedRef<boolean>
  isLibraryMigratedPlanOnTrial: ComputedRef<boolean>
  currentOfferedLibraryPlan: ComputedRef<Partial<Plan> | null>
  classroomPlanCurrentTierPrice: ComputedRef<string>

  changeStepLibraryDowngradeModal: ({ libraryDowngradeStep }: { libraryDowngradeStep: LibraryDowngradeStep }) => void
  closeClassroomLibraryDowngradeModal: () => void
  closeLibraryDowngradeModal: () => void
  downgradeLibraryToTeamNeonImmediately: () => void
  loadAndRedirectToLibraryChangeCardUrl: () => Promise<void>
  scheduleLibraryDowngradeToTeamNeon: () => void
  showLibraryDowngradeToTeamNeonModalCheckingQuota: ({
    tierQuota,
    wallsUsed,
  }: {
    tierQuota: number
    wallsUsed: number
  }) => void
  showUndoLibraryScheduledChangeModal: () => void
}

const useLibraryBilling = ({
  library: { currentLibrary, isClassroomLibrary, isTeamLibrary, libraryMakersCount },
  estimatedTeamNeonDowngradeRefund,
  libraryBilling,
}: { library: LibraryComposable } & LibraryBillingStateRefs): LibraryBillingComposable => {
  const libraryPlansStore = useLibraryPlansStore()

  // Getters
  const currentLibraryScheduledChange = computed((): LibraryScheduledChange | null => {
    if (!isPaidLibraryBilling(libraryBilling.value)) return null
    if (libraryBilling.value?.status?.is_cancelling_soon) return LibraryScheduledChange.DowngradeToTeamNeon
    if (libraryBilling.value?.switch_plan_status?.is_switching_soon) {
      return libraryBilling.value.period === 'year'
        ? LibraryScheduledChange.SwitchToMonthly
        : LibraryScheduledChange.SwitchToAnnual
    }
    if (libraryBilling.value?.switch_plan_quantity_status?.is_switching_soon) {
      return LibraryScheduledChange.ChangePlanQuantity
    }
    return null
  })
  const estimatedTeamNeonDowngradeRefundAmount = computed((): string | null => {
    if (estimatedTeamNeonDowngradeRefund.value == null) return null

    return translatePrice(
      parseFloat(estimatedTeamNeonDowngradeRefund.value.amount),
      estimatedTeamNeonDowngradeRefund.value.currency,
    )
  })
  const hasLibraryScheduledChanges = computed((): boolean => currentLibraryScheduledChange.value != null)
  // While a Team library's free-add window is open, the maker count intentionally diverges from
  // the billed count (billing sync is skipped), so the "pricing will be updated" banner is wrong.
  const hasPendingMakerCountUpdate = computed((): boolean => {
    const inFreeAddWindow =
      isPaidLibraryBilling(libraryBilling.value) && libraryBilling.value.in_free_add_window === true
    return (
      !inFreeAddWindow &&
      libraryUsersBilledCount.value !== 0 &&
      libraryUsersBilledCount.value !== libraryMakersCount.value
    )
  })
  const isLibraryClassroomGoldTier = computed(
    (): boolean => isClassroomLibrary.value && libraryBilling.value?.tier === LibraryMembershipTier.Gold,
  )
  const isLibraryOnV2ClassroomPlan = computed((): boolean => {
    if (!isClassroomLibrary.value) return false
    if (!isPaidLibraryBilling(libraryBilling.value)) return false
    return libraryBilling.value?.plan.plan_id?.includes('v2') ?? false
  })
  const isLibraryPaying = computed((): boolean => isLibraryTeamGoldTier.value || isLibraryClassroomGoldTier.value)
  const isLibraryTeamGoldTier = computed(
    (): boolean => isTeamLibrary.value && libraryBilling.value?.tier === LibraryMembershipTier.Gold,
  )
  const isLibraryTeamNeonTier = computed(
    (): boolean => isTeamLibrary.value && libraryBilling.value?.tier === LibraryMembershipTier.Neon,
  )
  const isLibraryWebPlan = computed(
    (): boolean => isPaidLibraryBilling(libraryBilling.value) && libraryBilling.value.payment_channel === 'chargebee',
  )
  const isOfferedTeamCurrencyEqualToCurrentCurrency = computed((): boolean => {
    if (!isLibraryPaying.value || !isPaidLibraryBilling(libraryBilling.value)) return true

    return (
      libraryPlansStore.plans.gold.annual.currency?.toLowerCase() ===
      libraryBilling.value?.plan?.currency?.toLowerCase()
    )
  })
  const librarySubscriptionStatus = computed((): SubscriptionStatus | null => {
    if (currentLibrary.value == null) return null
    if (currentLibrary.value.isDeprecatedFreeTier) return null
    if (currentLibrary.value.membershipTier === LibraryMembershipTier.Trial) return SubscriptionStatus.OnTrial
    if (currentLibrary.value.isTrialExpired === true) return SubscriptionStatus.TrialExpired
    if (currentLibrary.value.isPlanCancelled) return SubscriptionStatus.Cancelled
    if (currentLibrary.value.membershipTier === LibraryMembershipTier.Gold) return SubscriptionStatus.Paying
    return null
  })
  const libraryUsersBilledCount = computed((): number => {
    return isPaidLibraryBilling(libraryBilling.value) ? libraryBilling.value.users_billed_count : 0
  })
  const classroomPlanCurrentTierPrice = computed((): string => {
    // Current classroom plans are stairstep. Multiplying the price on
    // the frontend will have small rounding errors. Instead we use the plans
    // price tiers from ChargeBee.

    if (!isPaidLibraryBilling(libraryBilling.value) || (libraryBilling.value?.price_tiers?.length ?? 0) === 0) {
      return libraryPlansStore.plans.gold.annual.formattedPrice
    }

    const currentTier = libraryBilling.value.users_billed_count

    // If users_billed_count exceeds the highest tier, return the last tier's price
    const priceTiers = libraryBilling.value.price_tiers as StairstepPriceTier[]
    const lastTier = priceTiers[priceTiers.length - 1]

    if (currentTier >= lastTier.starting_unit) {
      return lastTier.formatted_price_with_currency
    }

    const currentPlanTier = priceTiers.find((tier) => {
      return currentTier >= tier.starting_unit && currentTier <= tier.ending_unit
    })

    return currentPlanTier?.formatted_price_with_currency ?? ''
  })
  const currentOfferedLibraryPlan = computed<Partial<Plan> | null>(() => {
    if (!isPaidLibraryBilling(libraryBilling.value)) return null
    return libraryPlansStore.plans[libraryBilling.value.tier][normalizedPaymentSchedule(libraryBilling.value.period)]
  })
  const isLibraryOnNewPlan = computed<boolean>(() => {
    if (currentOfferedLibraryPlan.value == null || !isPaidLibraryBilling(libraryBilling.value)) return false
    // A school library might not have a plan_id. Their plan tier is gold by default.
    if (currentLibrary.value?.libraryType === LibraryType.School && libraryBilling.value.plan?.plan_id == null)
      return false
    return currentOfferedLibraryPlan.value.id === libraryBilling.value.plan?.plan_id
  })
  const isLibraryGoldLegacyTier = computed<boolean>(
    () => libraryBilling.value?.tier === 'gold' && !isLibraryOnNewPlan.value && isLibraryWebPlan.value,
  )
  const isLibraryMigratedPlan = computed<boolean>(() => {
    if (!isPaidLibraryBilling(libraryBilling.value)) return false
    return !!libraryBilling.value.is_migrated_plan
  })
  // During tenant-to-library migration, a new subscription is created with a trial period ending
  // at the old subscription's renewal date. While on trial, both subscriptions coexist — changing
  // the plan could put them out of sync. After renewal the old subscription cancels and the new
  // one activates, so plan changes are safe again.
  const isLibraryMigratedPlanOnTrial = computed<boolean>(() => {
    if (!isPaidLibraryBilling(libraryBilling.value)) return false
    return isLibraryMigratedPlan.value && !!libraryBilling.value.status.is_in_trial
  })

  // Actions
  const changeStepLibraryDowngradeModal = (payload: { libraryDowngradeStep: LibraryDowngradeStep }): void => {
    void getVuexStore<DashboardSettingsState>()?.dispatch('changeStepLibraryDowngradeModal', payload, { root: true })
  }
  const closeClassroomLibraryDowngradeModal = (): void => {
    void getVuexStore<DashboardSettingsState>()?.dispatch('closeClassroomLibraryDowngradeModal', null, { root: true })
  }
  const closeLibraryDowngradeModal = (): void => {
    void getVuexStore<DashboardSettingsState>()?.dispatch('closeLibraryDowngradeModal', null, { root: true })
  }
  const downgradeLibraryToTeamNeonImmediately = (): void => {
    void getVuexStore<DashboardSettingsState>()?.dispatch('downgradeLibraryToTeamNeonImmediately', null, { root: true })
  }
  const loadAndRedirectToLibraryChangeCardUrl = async (): Promise<void> => {
    void getVuexStore<DashboardSettingsState>()?.dispatch('loadAndRedirectToLibraryChangeCardUrl', null, { root: true })
  }
  const scheduleLibraryDowngradeToTeamNeon = (): void => {
    void getVuexStore<DashboardSettingsState>()?.dispatch('scheduleLibraryDowngradeToTeamNeon', null, { root: true })
  }
  const showLibraryDowngradeToTeamNeonModalCheckingQuota = (payload: {
    tierQuota: number
    wallsUsed: number
  }): void => {
    void getVuexStore<DashboardSettingsState>()?.dispatch('showLibraryDowngradeToTeamNeonModalCheckingQuota', payload, {
      root: true,
    })
  }
  const showUndoLibraryScheduledChangeModal = (): void => {
    void getVuexStore<DashboardSettingsState>()?.dispatch('showUndoLibraryScheduledChangeModal', null, { root: true })
  }

  return {
    currentLibraryScheduledChange,
    estimatedTeamNeonDowngradeRefundAmount,
    hasLibraryScheduledChanges,
    hasPendingMakerCountUpdate,
    isLibraryClassroomGoldTier,
    isLibraryOnV2ClassroomPlan,
    isLibraryPaying,
    isLibraryTeamGoldTier,
    isLibraryTeamNeonTier,
    isLibraryWebPlan,
    isOfferedTeamCurrencyEqualToCurrentCurrency,
    librarySubscriptionStatus,
    libraryUsersBilledCount,
    isLibraryGoldLegacyTier,
    isLibraryMigratedPlan,
    isLibraryMigratedPlanOnTrial,
    currentOfferedLibraryPlan,
    classroomPlanCurrentTierPrice,

    changeStepLibraryDowngradeModal,
    closeClassroomLibraryDowngradeModal,
    closeLibraryDowngradeModal,
    downgradeLibraryToTeamNeonImmediately,
    loadAndRedirectToLibraryChangeCardUrl,
    scheduleLibraryDowngradeToTeamNeon,
    showLibraryDowngradeToTeamNeonModalCheckingQuota,
    showUndoLibraryScheduledChangeModal,
  }
}

type BillingState = Billing | null

interface PersonalBillingComposable {
  isBillingInitialized: ComputedRef<boolean>
  isCancellingSoon: ComputedRef<boolean>
  isNeonTier: ComputedRef<boolean>
}

const usePersonalBilling = (
  billing: Ref<BillingState>,
  _invoices: Ref<LibraryInvoice[]>,
): PersonalBillingComposable => {
  const isBillingInitialized = computed<boolean>(() => billing.value != null)
  const isCancellingSoon = computed<boolean>(() => {
    if (!isPaidBilling(billing.value)) return false

    return billing.value.status.is_cancelling_soon
  })
  const isNeonTier = computed<boolean>(() => !isPaidBilling(billing.value))

  return {
    isBillingInitialized,
    isCancellingSoon,
    isNeonTier,
  }
}

interface PersonalPlansStateRefs {
  cancelReasonText: Ref<string>
  crossgradeError: Ref<string>
  crossgradeStep: Ref<CrossgradeStep | null>
  downgradeError: Ref<string>
  downgradeStep: Ref<DowngradeStep | null>
  estimatedCancellationRefund: Ref<EstimateCancellationRefundResult | null>
  estimatedCrossgradeCost: Ref<EstimateSwitchingCostResult | null>
  estimatedGoldDowngradeCost: Ref<EstimateSwitchingCostResult | null>
  estimatedSilverDowngradeCost: Ref<EstimateSwitchingCostResult | null>
  isLoadingCancellationRefundInformation: Ref<boolean>
  isLoadingCrossgradeInformation: Ref<boolean>
  isLoadingGoldDowngradeInformation: Ref<boolean>
  isLoadingSilverDowngradeInformation: Ref<boolean>
  isProcessingCrossgrade: Ref<boolean>
  isProcessingDowngrade: Ref<boolean>
  xCrossgradeModal: Ref<boolean>
  xDowngradeModal: Ref<boolean>
}

interface PersonalPlansComposable {
  crossgradeError: Ref<string>
  crossgradeStep: Ref<CrossgradeStep | null>
  currentScheduledChange: ComputedRef<ScheduledChange | null>
  currentScheduleGoldPlan: ComputedRef<Partial<Plan> | null>
  currentScheduleSilverPlan: ComputedRef<Partial<Plan> | null>
  displayedEstimatedCrossgradeCost: ComputedRef<EstimateSwitchingCostResult>
  displayedEstimatedGoldDowngradeCost: ComputedRef<EstimateSwitchingCostResult>
  displayedEstimatedSilverDowngradeCost: ComputedRef<EstimateSwitchingCostResult>
  cancelReasonText: Ref<string>
  downgradeError: Ref<string>
  downgradeStep: Ref<DowngradeStep | null>
  estimatedCancellationRefundAmount: ComputedRef<string | null>
  estimatedCrossgradeCost: Ref<EstimateSwitchingCostResult | null>
  estimatedGoldDowngradeCost: Ref<EstimateSwitchingCostResult | null>
  oppositeSchedulePlan: ComputedRef<Partial<Plan> | null>
  hasScheduledChanges: ComputedRef<boolean>
  isGoldLegacyTier: ComputedRef<boolean>
  isLegacyTier: ComputedRef<(tier: string) => boolean>
  isLoadingCancellationRefundInformation: Ref<boolean>
  isMigratedPlan: ComputedRef<boolean>
  isMigratedPlanOnTrial: ComputedRef<boolean>
  isMobilePlan: ComputedRef<boolean>
  isApplePlan: ComputedRef<boolean>
  isLoadingCrossgradeInformation: Ref<boolean>
  isLoadingGoldDowngradeInformation: Ref<boolean>
  isLoadingSilverDowngradeInformation: Ref<boolean>
  isOfferedPlanCurrencyEqualToUserPlanCurrency: ComputedRef<boolean>
  isPlatinumLegacyTier: ComputedRef<boolean>
  isPlatinumResellerTier: ComputedRef<boolean>
  isProcessingCrossgrade: Ref<boolean>
  isProcessingDowngrade: Ref<boolean>
  isWebPlan: ComputedRef<boolean>
  xCrossgradeModal: Ref<boolean>
  xDowngradeModal: Ref<boolean>
  cancelImmediately: () => void
  closePlanCrossgradeModal: () => void
  closePlanDowngradeModal: () => void
  doNotRenew: () => void
  downgradePlatinumToGoldDifferentCurrencyImmediately: () => void
  downgradePlatinumToGoldImmediately: () => void
  schedulePlatinumToGoldDowngrade: () => void
  downgradeToSilverDifferentCurrencyImmediately: () => void
  downgradeToSilverImmediately: () => void
  scheduleToSilverDowngrade: () => void
  scheduleResellerSwitch: (planId: string) => void
  scheduleSwitch: () => void
  showPlanCrossgradeModalCheckingCurrency: (payload: { userPlanCurrency: string }) => void
  showPlanDowngradeToNeonModalCheckingQuota: (payload: { tierQuota: number; wallsUsed: number }) => void
  showPlanDowngradeToGoldModalCheckingQuota: (payload: {
    tierQuota: number
    wallsUsed: number
    userPlanCurrency: string
  }) => void
  showPlanDowngradeToSilverModalCheckingQuota: (payload: {
    tierQuota: number
    wallsUsed: number
    userPlanCurrency: string
  }) => void
  showScheduledChangeModal: () => void
  switchDifferentCurrencyImmediately: () => void
  switchImmediately: () => void
}

const usePersonalPlans = (
  billing: Ref<BillingState>,
  {
    cancelReasonText,
    crossgradeError,
    crossgradeStep,
    downgradeError,
    downgradeStep,
    estimatedCancellationRefund,
    estimatedCrossgradeCost,
    estimatedGoldDowngradeCost,
    estimatedSilverDowngradeCost,
    isLoadingCancellationRefundInformation,
    isLoadingCrossgradeInformation,
    isLoadingGoldDowngradeInformation,
    isLoadingSilverDowngradeInformation,
    isProcessingCrossgrade,
    isProcessingDowngrade,
    xCrossgradeModal,
    xDowngradeModal,
  }: PersonalPlansStateRefs,
): PersonalPlansComposable => {
  const personalPlansStore = usePersonalPlansStore()

  const currentOfferedPlan = computed<Partial<Plan> | null>(() => {
    if (!isPaidBilling(billing.value)) return null
    return personalPlansStore.plans[billing.value.tier][normalizedPaymentSchedule(billing.value.period)]
  })

  const currentScheduleGoldPlan = computed<Partial<Plan> | null>(() => {
    if (!isPaidBilling(billing.value)) return null
    return personalPlansStore.plans.gold[normalizedPaymentSchedule(billing.value.period)]
  })

  const currentScheduleSilverPlan = computed<Partial<Plan> | null>(() => {
    if (!isPaidBilling(billing.value)) return null
    return personalPlansStore.plans.silver[normalizedPaymentSchedule(billing.value.period)]
  })

  const isGoldLegacyTier = computed<boolean>(
    () => isLegacyTier.value('gold') || (billing.value?.tier === 'gold' && isAppUsing('deprecateGoldPlans')),
  )

  const isLegacyTier = computed<(tier: string) => boolean>(
    () => (tier: string) => billing.value?.tier === tier && !isOnNewPlan.value && isWebPlan.value,
  )

  const isOfferedPlanCurrencyEqualToUserPlanCurrency = computed<boolean>(() => {
    if (!isPaidBilling(billing.value)) return false
    return currentOfferedPlan.value?.currency === billing.value.plan.currency
  })

  const isOnNewPlan = computed<boolean>(() => {
    if (currentOfferedPlan.value == null || !isPaidBilling(billing.value)) return false

    return currentOfferedPlan.value.id === billing.value.plan.plan_id
  })

  const isPurchasedViaReseller = computed<boolean>(() => {
    if (!isPaidBilling(billing.value) || billing.value.is_sponsored) return false
    return billing.value.status.is_purchased_via_reseller
  })

  const isPlatinumLegacyTier = computed<boolean>(() => !isPurchasedViaReseller.value && isLegacyTier.value('platinum'))

  const isPlatinumResellerTier = computed<boolean>(() => {
    if (!isPaidBilling(billing.value)) return false
    return isPurchasedViaReseller.value && billing.value.tier === 'platinum'
  })

  const oppositeSchedulePlan = computed<Partial<Plan> | null>(() => {
    if (!isPaidBilling(billing.value)) return null
    return personalPlansStore.plans[billing.value.tier][normalizedOppositePaymentSchedule(billing.value.period)]
  })

  const isWebPlan = computed<boolean>(() => isWebBilling(billing.value))

  const isMigratedPlan = computed<boolean>(() => {
    if (!isPaidBilling(billing.value)) return false
    return !!billing.value.status.is_migrated_plan
  })
  // During tenant-to-library migration, a new subscription is created with a trial period ending
  // at the old subscription's renewal date. While on trial, both subscriptions coexist — changing
  // the plan could put them out of sync. After renewal the old subscription cancels and the new
  // one activates, so plan changes are safe again.
  const isMigratedPlanOnTrial = computed<boolean>(() => {
    if (!isPaidBilling(billing.value)) return false
    return isMigratedPlan.value && !!billing.value.status.is_in_trial
  })

  const isMobilePlan = computed<boolean>(() => {
    if (!isPaidBilling(billing.value)) return false
    return billing.value.payment_channel === 'ios' || billing.value.payment_channel === 'android'
  })

  const isApplePlan = computed<boolean>(() => {
    return isPaidBilling(billing.value) && billing.value.payment_channel === 'ios'
  })

  // Plan cancellation
  const estimatedCancellationRefundAmount = computed<string | null>(() => {
    if (estimatedCancellationRefund.value == null) return null

    return translatePrice(
      parseFloat(estimatedCancellationRefund.value.amount),
      estimatedCancellationRefund.value.currency,
    )
  })

  function cancelImmediately(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('cancelImmediately')
  }

  // Plan crossgrade
  function closePlanCrossgradeModal(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('closePlanCrossgradeModal')
  }

  function scheduleSwitch(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('scheduleSwitch')
  }

  function showPlanCrossgradeModalCheckingCurrency(payload: { userPlanCurrency: string }): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('showPlanCrossgradeModalCheckingCurrency', payload, {
      root: true,
    })
  }

  function switchDifferentCurrencyImmediately(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('switchDifferentCurrencyImmediately')
  }

  function scheduleResellerSwitch(planId: string): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('scheduleResellerSwitch', { planId })
  }

  function switchImmediately(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('switchImmediately')
  }

  // Plan scheduled changes
  const currentScheduledChange = computed<ScheduledChange | null>(() => {
    if (!isPaidBilling(billing.value)) return null
    if (billing.value.status.is_cancelling_soon) return ScheduledChange.DowngradeToNeon
    if (billing.value.switch_plan_status.is_switching_soon) {
      // Reseller switches to standard plans (check before downgrades since reseller plans overlap with standard plan IDs)
      if (billing.value.status.is_purchased_via_reseller) {
        const newPlanId = billing.value.switch_plan_status.new_plan_id
        if (
          newPlanId === personalPlansStore.plans.platinum.monthly.id ||
          newPlanId === personalPlansStore.plans.platinum.annual.id
        ) {
          return ScheduledChange.SwitchToPlatinum
        }
        if (
          newPlanId === personalPlansStore.plans.gold.monthly.id ||
          newPlanId === personalPlansStore.plans.gold.annual.id
        ) {
          return ScheduledChange.SwitchToGold
        }
      }
      if (
        billing.value.switch_plan_status.new_plan_id ===
        personalPlansStore.plans.gold[normalizedPaymentSchedule(billing.value.period)].id
      ) {
        return ScheduledChange.DowngradeToGold
      }
      if (
        billing.value.switch_plan_status.new_plan_id ===
        personalPlansStore.plans.silver[normalizedPaymentSchedule(billing.value.period)].id
      ) {
        return ScheduledChange.DowngradeToSilver
      }
      return billing.value.period === 'year' ? ScheduledChange.SwitchToMonthly : ScheduledChange.SwitchToAnnual
    }
    return null
  })

  const hasScheduledChanges = computed<boolean>(() => currentScheduledChange.value != null)

  function showScheduledChangeModal(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('showScheduledChangeModal')
  }

  // Plan downgrade
  function closePlanDowngradeModal(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('closePlanDowngradeModal')
  }

  function doNotRenew(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('doNotRenew')
  }

  function downgradePlatinumToGoldDifferentCurrencyImmediately(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('downgradePlatinumToGoldDifferentCurrencyImmediately')
  }

  function downgradePlatinumToGoldImmediately(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('downgradePlatinumToGoldImmediately')
  }

  function schedulePlatinumToGoldDowngrade(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('schedulePlatinumToGoldDowngrade')
  }

  function downgradeToSilverDifferentCurrencyImmediately(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('downgradeToSilverDifferentCurrencyImmediately')
  }

  function downgradeToSilverImmediately(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('downgradeToSilverImmediately')
  }

  function scheduleToSilverDowngrade(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('scheduleToSilverDowngrade')
  }

  function showPlanDowngradeToNeonModalCheckingQuota(payload: { tierQuota: number; wallsUsed: number }): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('showPlanDowngradeToNeonModalCheckingQuota', payload)
  }

  function showPlanDowngradeToGoldModalCheckingQuota(payload: {
    tierQuota: number
    wallsUsed: number
    userPlanCurrency: string
  }): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('showPlanDowngradeToGoldModalCheckingQuota', payload)
  }

  function showPlanDowngradeToSilverModalCheckingQuota(payload: {
    tierQuota: number
    wallsUsed: number
    userPlanCurrency: string
  }): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('showPlanDowngradeToSilverModalCheckingQuota', payload)
  }

  // Plan details, normalized for display
  const emptyEstimatedDowngradeCost = {
    is_refund: false,
    currency_code: '',
    new_plan_cost: '',
    unused_portion: '',
    refund_amount: '',
    amount_due: '',
    invoice_amount: '',
  }

  const displayedEstimatedGoldDowngradeCost = computed<EstimateSwitchingCostResult>(() => {
    if (estimatedGoldDowngradeCost.value == null) return emptyEstimatedDowngradeCost
    return estimatedGoldDowngradeCost.value
  })

  const displayedEstimatedSilverDowngradeCost = computed<EstimateSwitchingCostResult>(() => {
    if (estimatedSilverDowngradeCost.value == null) return emptyEstimatedDowngradeCost
    return estimatedSilverDowngradeCost.value
  })

  const displayedEstimatedCrossgradeCost = computed<EstimateSwitchingCostResult>(() => {
    if (estimatedCrossgradeCost.value == null) {
      return {
        is_refund: false,
        currency_code: '',
        new_plan_cost: '',
        unused_portion: '',
        refund_amount: '',
        amount_due: '',
        invoice_amount: '',
      }
    }
    return estimatedCrossgradeCost.value
  })

  return {
    cancelReasonText,
    crossgradeError,
    crossgradeStep,
    currentScheduledChange,
    currentScheduleGoldPlan,
    currentScheduleSilverPlan,
    displayedEstimatedCrossgradeCost,
    displayedEstimatedGoldDowngradeCost,
    displayedEstimatedSilverDowngradeCost,
    downgradeError,
    downgradeStep,
    estimatedCancellationRefundAmount,
    estimatedCrossgradeCost,
    estimatedGoldDowngradeCost,
    hasScheduledChanges,
    isGoldLegacyTier,
    isLegacyTier,
    isLoadingCancellationRefundInformation,
    isLoadingCrossgradeInformation,
    isLoadingGoldDowngradeInformation,
    isLoadingSilverDowngradeInformation,
    isMigratedPlan,
    isMigratedPlanOnTrial,
    isOfferedPlanCurrencyEqualToUserPlanCurrency,
    isPlatinumLegacyTier,
    isPlatinumResellerTier,
    isProcessingCrossgrade,
    isProcessingDowngrade,
    isMobilePlan,
    isApplePlan,
    isWebPlan,
    oppositeSchedulePlan,
    xCrossgradeModal,
    xDowngradeModal,

    cancelImmediately,
    closePlanCrossgradeModal,
    closePlanDowngradeModal,
    doNotRenew,
    downgradePlatinumToGoldDifferentCurrencyImmediately,
    downgradePlatinumToGoldImmediately,
    schedulePlatinumToGoldDowngrade,
    downgradeToSilverDifferentCurrencyImmediately,
    downgradeToSilverImmediately,
    scheduleToSilverDowngrade,
    scheduleResellerSwitch,
    scheduleSwitch,
    showPlanCrossgradeModalCheckingCurrency,
    showPlanDowngradeToGoldModalCheckingQuota,
    showPlanDowngradeToSilverModalCheckingQuota,
    showPlanDowngradeToNeonModalCheckingQuota,
    showScheduledChangeModal,
    switchDifferentCurrencyImmediately,
    switchImmediately,
  }
}
export {
  GenericStep,
  LibraryGenericStep,
  LibraryScheduledChange,
  ScheduledChange,
  unableToProcessErrorMessage,
  unpaidInvoiceErrorMessage,
  useDashboardSettingsStore,
}
export type { BillingState, CrossgradeStep, DowngradeStep, LibraryDowngradeStep }
